import React from 'react';
import { CreditCard, Smartphone, Globe, Shield } from 'lucide-react';

const HowToPayPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-slate-800 mb-8">How To Pay</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-lg text-gray-700 mb-8">
            We offer multiple secure payment methods to make your booking process as convenient as possible.
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            
            {/* Online Payments */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <CreditCard className="w-8 h-8 text-blue-600 mr-3" />
                <h2 className="text-2xl font-semibold text-slate-800">Online Payments</h2>
              </div>
              <ul className="space-y-2 text-gray-700">
                <li>• Credit Cards (Visa, Mastercard, American Express)</li>
                <li>• Debit Cards</li>
                <li>• PayPal</li>
                <li>• Apple Pay</li>
                <li>• Google Pay</li>
              </ul>
            </div>

            {/* Bank Transfer */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <Globe className="w-8 h-8 text-green-600 mr-3" />
                <h2 className="text-2xl font-semibold text-slate-800">Bank Transfer</h2>
              </div>
              <ul className="space-y-2 text-gray-700">
                <li>• Direct bank transfer</li>
                <li>• Standing orders for regular appointments</li>
                <li>• International wire transfers accepted</li>
                <li>• BACS payments</li>
              </ul>
            </div>

            {/* Mobile Payments */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <Smartphone className="w-8 h-8 text-purple-600 mr-3" />
                <h2 className="text-2xl font-semibold text-slate-800">Mobile Payments</h2>
              </div>
              <ul className="space-y-2 text-gray-700">
                <li>• Apple Pay</li>
                <li>• Google Pay</li>
                <li>• Samsung Pay</li>
                <li>• Contactless payments</li>
              </ul>
            </div>

            {/* Security */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <Shield className="w-8 h-8 text-orange-600 mr-3" />
                <h2 className="text-2xl font-semibold text-slate-800">Security</h2>
              </div>
              <ul className="space-y-2 text-gray-700">
                <li>• SSL encrypted transactions</li>
                <li>• PCI DSS compliant</li>
                <li>• Secure payment processing</li>
                <li>• Data protection compliant</li>
              </ul>
            </div>
          </div>

          <div className="bg-gray-50 border-l-4 border-gray-400 p-6 mb-8">
            <h3 className="text-xl font-semibold text-slate-800 mb-4">Payment Process</h3>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              <li>Select your preferred appointment time and service</li>
              <li>Choose your payment method at checkout</li>
              <li>Complete the secure payment process</li>
              <li>Receive confirmation email with appointment details</li>
              <li>Get reminder notifications before your appointment</li>
            </ol>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-slate-800 mb-4">Important Notes</h3>
            <ul className="space-y-2 text-gray-700">
              <li>• Payment is required at the time of booking</li>
              <li>• Refunds are processed within 3-5 business days</li>
              <li>• Currency: All payments are processed in GBP (£)</li>
              <li>• For payment issues, contact our support team</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowToPayPage;